import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { useToastStore } from '@/stores/toast-store';
import { AdminApiError } from './api-client';
import { getErrorToastPayload } from './global-error-handler';

/**
 * グローバルエラーハンドラー
 *
 * TanStack Query の QueryCache / MutationCache レベルでエラーを
 * キャッチし、トースト通知を発行する。
 *
 * Zustand の `getState()` により React ツリー外からもトーストを追加可能。
 */
function handleGlobalError(error: unknown): void {
  // 401 はAPI Client側でリダイレクト済みのため、重複通知を防ぐ
  if (error instanceof AdminApiError && error.status === 401) {
    return;
  }

  const payload = getErrorToastPayload(error);
  useToastStore.getState().addToast(payload);
}

const queryCache = new QueryCache({
  onError: (error, query) => {
    // meta.suppressGlobalError が設定されたクエリはスキップ
    if (query.meta?.suppressGlobalError) return;
    handleGlobalError(error);
  },
});

const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    // meta.suppressGlobalError が設定されたミューテーションはスキップ
    if (mutation.meta?.suppressGlobalError) return;
    handleGlobalError(error);
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export { queryClient };
